import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  inject,
  input,
  OnInit,
  output,
  OutputEmitterRef,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';

import { MatRadioGroup } from '@angular/material/radio';
import { ButtonDirective } from '@portal/ui-common';
import {
  InputDirective,
  InputPatternDirective,
  LabelDirective,
  RadioComponent,
  ValidationMessageComponent,
  ValidationMessagesComponent,
} from '@shared/ui-forms';
import { IconComponent } from '@shared/ui-shared';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { distinctUntilChanged, filter, map, tap } from 'rxjs';
import { AdditionalAccountHolder } from '../../models/additional-account-holder.model';
import { ContactMethod } from '../../models/contact-method.model';
import { PersonalDetails } from '../../models/personal-details.model';
import { RequestBillingAddress } from '../../models/request-billing-address.model';
import { PERSONAL_DETAILS_MOCK } from './personal-details.mock';

const CSL_IMPORTS = [
  InputDirective,
  InputPatternDirective,
  ValidationMessagesComponent,
  ValidationMessageComponent,
  LabelDirective,
  RadioComponent,
  IconComponent,
  ButtonDirective,
];

type PersonalDetailsFormControls = {
  firstName: FormControl<string | null>;
  lastName: FormControl<string | null>;
  phone: FormControl<string | null>;
  email: FormControl<string | null>;
  preferredContactMethod: FormControl<ContactMethod | null>;
  firstNameHolder: FormControl<string | null>;
  lastNameHolder: FormControl<string | null>;
  phoneHolder: FormControl<string | null>;
};

export type PersonalDetailsForm = FormGroup<PersonalDetailsFormControls>;
type PersonalDetailsFormControlsNames = keyof PersonalDetailsFormControls;

@Component({
  selector: 'csl-personal-details',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgxMaskDirective, MatRadioGroup, ...CSL_IMPORTS],
  templateUrl: './personal-details.component.html',
  styleUrl: './personal-details.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [provideNgxMask()],
})
export class PersonalDetailsComponent implements OnInit {
  readonly destroyRef = inject(DestroyRef);

  @ViewChild('additionalHolderButton') private additionalHolderButton?: ElementRef<HTMLButtonElement>;
  @ViewChild('firstNameHolderInput') private firstNameHolderInput?: ElementRef<HTMLInputElement>;

  readonly namesPattern = /^[a-zA-Z' -]*$/;
  readonly emailPattern = /^[a-zA-Z0-9'_@.-]*$/;
  readonly emailValidationPattern =
    /^(?!\.)(?!.*\.\.)[a-zA-Z0-9'._-]{1,64}(?<!\.)@[a-zA-Z0-9-]{1,255}(?:\.[a-zA-Z0-9-]{1,255})+$/;
  readonly contactMethod = ContactMethod;

  showSupportiveText = input<boolean>(true);
  isDisabled = input<boolean>(false);
  billingAddress = input<RequestBillingAddress | null>(null);
  additionalHolder = input<AdditionalAccountHolder | null>(null);
  firstName = input<string>('');
  lastName = input<string>('');
  preferredWayOfCommunication = input<ContactMethod | null>(null);

  form: PersonalDetailsForm | null = null;
  showHolder = false;

  readonly errorMessages: { [key in PersonalDetailsFormControlsNames]: { [key: string]: string } } = {
    firstName: {
      required: 'First name is required.',
      minlength: 'First name must be 2-35 characters.',
      pattern: "First name can only use A-Z, ('), (-), and spaces (no accents).",
    },
    lastName: {
      required: 'Last name is required.',
      minlength: 'Last name must be 2-35 characters.',
      pattern: "Last name can only use A-Z, ('), (-), and spaces (no accents).",
    },
    phone: {
      required: 'Phone number is required.',
      mask: 'Phone number is invalid. Enter a 10-digit phone number.',
    },
    email: {
      required: 'Email is required.',
      pattern: 'Email format is invalid. Enter a valid email address.',
    },
    firstNameHolder: {
      minlength: 'First name must be 2-35 characters.',
      pattern: "First name can only use A-Z, ('), (-), and spaces (no accents).",
    },
    lastNameHolder: {
      minlength: 'Last name must be 2-35 characters.',
      pattern: "Last name can only use A-Z, ('), (-), and spaces (no accents).",
    },
    phoneHolder: {
      mask: 'Phone number is invalid. Enter a 10-digit phone number.',
    },
    preferredContactMethod: {},
  };

  readonly errorIds: Record<PersonalDetailsFormControlsNames, string> = {
    firstName: 'firstName-error',
    lastName: 'lastName-error',
    phone: 'phone-error',
    email: 'email-error',
    firstNameHolder: 'firstNameHolder-error',
    lastNameHolder: 'lastNameHolder-error',
    phoneHolder: 'phoneHolder-error',
    preferredContactMethod: 'preferredContactMethod-error',
  };

  readonly personalDetailsChange = output<PersonalDetails | null>();
  firstNameError = output<string>();
  lastNameError = output<string>();
  phoneError = output<string>();
  emailError = output<string>();
  firstNameHolderError = output<string>();
  lastNameHolderError = output<string>();
  phoneHolderError = output<string>();

  ngOnInit(): void {
    this.form = this.initForm();
    if (this.isDisabled()) {
      this.form.disable();
    }
    this.initialEmit(); // emit initial value back to make the parent component valid
    this.setSubscriptions();
  }

  isValidationShouldBeShown(control: AbstractControl): boolean {
    return control.invalid && control.touched;
  }

  validationShouldBeShown(control: AbstractControl): boolean {
    return this.isValidationShouldBeShown(control) || (control.invalid && control.errors?.['pattern']);
  }

  setTestFormValues(): void {
    this.form?.setValue(PERSONAL_DETAILS_MOCK);
  }

  addHolder(): void {
    if (this.isDisabled()) {
      return;
    }

    this.showHolder = true;
    setTimeout(() => this.firstNameHolderInput?.nativeElement.focus());
  }

  removeHolder(): void {
    if (this.isDisabled()) {
      return;
    }

    this.showHolder = false;
    this.form?.patchValue({
      firstNameHolder: null,
      lastNameHolder: null,
      phoneHolder: null,
    });
    setTimeout(() => this.additionalHolderButton?.nativeElement.focus());
  }

  private initForm(): PersonalDetailsForm {
    const { phoneNumber, email } = this.billingAddress() || {};
    const { firstName, lastName, phoneNumber: phoneHolder } = this.additionalHolder() || {};

    this.showHolder = !!(firstName || lastName || phoneHolder);

    return new FormGroup({
      firstName: new FormControl<string | null>(this.firstName() ?? null, [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(this.namesPattern),
      ]),
      lastName: new FormControl<string | null>(this.lastName() ?? null, [
        Validators.required,
        Validators.minLength(2),
        Validators.pattern(this.namesPattern),
      ]),
      phone: new FormControl<string | null>(phoneNumber ?? null, Validators.required),
      email: new FormControl<string | null>(email ?? null, [
        Validators.required,
        Validators.pattern(this.emailValidationPattern),
      ]),
      preferredContactMethod: new FormControl<ContactMethod | null>(this.preferredWayOfCommunication() ?? null),
      firstNameHolder: new FormControl<string | null>(firstName ?? null, [
        Validators.minLength(2),
        Validators.pattern(this.namesPattern),
      ]),
      lastNameHolder: new FormControl<string | null>(lastName ?? null, [
        Validators.minLength(2),
        Validators.pattern(this.namesPattern),
      ]),
      phoneHolder: new FormControl<string | null>(phoneHolder ?? null),
    });
  }

  private initialEmit(): void {
    const result = this?.form?.valid ? (this.form?.value as PersonalDetails) : null;
    this.personalDetailsChange.emit(result);
  }

  private emitControlError(controlName: PersonalDetailsFormControlsNames, output: OutputEmitterRef<string>): void {
    const ctrl = this.form?.controls[controlName] as FormControl;

    ctrl.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ source }) => source),
        map((ctrl) => {
          const errorKey = Object.keys(ctrl.errors ?? {})[0]; // get first error

          if (this.isValidationShouldBeShown(ctrl)) {
            const error = this.errorMessages[controlName][errorKey];
            if (error) {
              return error;
            }
          }
          return '';
        }),
        distinctUntilChanged(),
        filter((error) => !!error),
        tap((error) => output.emit(error)),
      )
      .subscribe();
  }

  private setSubscriptions(): void {
    this.form?.valueChanges
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        distinctUntilChanged(),
        map((v) => {
          const result = this?.form?.valid ? (v as PersonalDetails) : null;
          this.personalDetailsChange.emit(result);
        }),
        //tap((v) => console.log('personalDetailsChange', v)),
      )
      .subscribe();

    this.emitControlError('firstName', this.firstNameError);
    this.emitControlError('lastName', this.lastNameError);
    this.emitControlError('phone', this.phoneError);
    this.emitControlError('email', this.emailError);
    this.emitControlError('firstNameHolder', this.firstNameHolderError);
    this.emitControlError('lastNameHolder', this.lastNameHolderError);
    this.emitControlError('phoneHolder', this.phoneHolderError);
  }
}
